import { useState, useEffect, useMemo } from 'react'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import {
  getIngredients,
  saveIngredient,
  deleteIngredient,
  getRecipes,
  saveRecipe,
  deleteRecipe,
  calcCostPerUsableUnit,
  calcRecipeTotals,
  calcBulkBreakdown,
} from '../lib/costingService'
import DateInput from '../components/ui/DateInput'
import {
  Package,
  Calculator,
  FlaskConical,
  BarChart3,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Search,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react'

// ─── Constants ───

const TABS = [
  { key: 'ingredients', label: 'Ingredients', icon: Package },
  { key: 'recipes', label: 'Recipes', icon: FlaskConical },
  { key: 'bulk', label: 'Bulk Simulation', icon: Calculator },
  { key: 'summary', label: 'Summary', icon: BarChart3 },
]

const USABLE_UNITS = ['g', 'ml', 'pieces', 'kg', 'L']

const EMPTY_INGREDIENT = {
  name: '',
  purchaseUnit: '',
  purchaseQty: '',
  purchaseCost: '',
  usableUnit: 'g',
  usableQtyPerUnit: '',
  wastePercent: 0,
  supplier: '',
  purchaseDate: '',
}

const EMPTY_RECIPE = {
  name: '',
  ingredients: [],
  portions: 1,
  targetMargin: 65,
}

const EMPTY_BULK = {
  packDescription: '',
  packSize: '',
  packUnit: 'g',
  numPacks: '',
  totalCost: '',
  usageDays: '',
  singleUnitCost: '',
}

// ─── Helpers ───

function fmtR(v) {
  const n = Number(v) || 0
  return `R${n.toFixed(2)}`
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5 text-orange-500" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}

// ─── Main Component ───

export default function CostingPage() {
  const { currentDepartmentId, currentDepartment } = useDepartment()
  const { addToast } = useToast()
  const { user } = useAuth()

  const [activeTab, setActiveTab] = useState('ingredients')

  // Data
  const [ingredients, setIngredients] = useState([])
  const [recipes, setRecipes] = useState([])
  const [loadingData, setLoadingData] = useState(false)

  // Load data when department changes
  useEffect(() => {
    if (!currentDepartmentId) return
    let cancelled = false
    async function load() {
      setLoadingData(true)
      try {
        const [ings, recs] = await Promise.all([
          getIngredients(currentDepartmentId),
          getRecipes(currentDepartmentId),
        ])
        if (!cancelled) {
          setIngredients(ings)
          setRecipes(recs)
        }
      } catch {
        if (!cancelled) addToast('Failed to load costing data', 'error')
      } finally {
        if (!cancelled) setLoadingData(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [currentDepartmentId])

  if (!currentDepartmentId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Package size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">Select a department to view costing</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Costing</h1>
          <p className="text-sm text-gray-400 mt-1">
            {currentDepartment?.name || currentDepartmentId} — ingredient &amp; recipe costing
          </p>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(t => {
            const Icon = t.icon
            const active = activeTab === t.key
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Loading overlay */}
        {loadingData && (
          <div className="flex items-center justify-center py-12">
            <Spinner />
            <span className="ml-3 text-gray-400">Loading costing data...</span>
          </div>
        )}

        {/* Tab content */}
        {!loadingData && activeTab === 'ingredients' && (
          <IngredientsTab
            departmentId={currentDepartmentId}
            ingredients={ingredients}
            setIngredients={setIngredients}
            addToast={addToast}
          />
        )}
        {!loadingData && activeTab === 'recipes' && (
          <RecipesTab
            departmentId={currentDepartmentId}
            ingredients={ingredients}
            recipes={recipes}
            setRecipes={setRecipes}
            addToast={addToast}
          />
        )}
        {!loadingData && activeTab === 'bulk' && <BulkSimulationTab />}
        {!loadingData && activeTab === 'summary' && (
          <SummaryTab ingredients={ingredients} recipes={recipes} />
        )}
      </div>
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 1: INGREDIENTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function IngredientsTab({ departmentId, ingredients, setIngredients, addToast }) {
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ ...EMPTY_INGREDIENT })
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  const filtered = useMemo(() => {
    if (!search.trim()) return ingredients
    const q = search.toLowerCase()
    return ingredients.filter(
      i => i.name?.toLowerCase().includes(q) || i.supplier?.toLowerCase().includes(q)
    )
  }, [ingredients, search])

  // Real-time cost preview
  const previewCost = useMemo(() => {
    return calcCostPerUsableUnit(form.purchaseCost, form.usableQtyPerUnit, form.wastePercent)
  }, [form.purchaseCost, form.usableQtyPerUnit, form.wastePercent])

  function updateForm(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function openNew() {
    setForm({ ...EMPTY_INGREDIENT })
    setEditingId(null)
    setShowForm(true)
  }

  function openEdit(ing) {
    setForm({
      ...EMPTY_INGREDIENT,
      ...ing,
    })
    setEditingId(ing.id)
    setShowForm(true)
  }

  function cancelForm() {
    setShowForm(false)
    setEditingId(null)
    setForm({ ...EMPTY_INGREDIENT })
  }

  async function handleSave() {
    if (!form.name.trim()) {
      addToast('Ingredient name is required', 'error')
      return
    }
    if (!form.usableQtyPerUnit || Number(form.usableQtyPerUnit) <= 0) {
      addToast('Usable quantity per unit is required', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = editingId ? { ...form, id: editingId } : { ...form }
      const saved = await saveIngredient(departmentId, payload)
      setIngredients(prev => {
        const idx = prev.findIndex(i => i.id === saved.id)
        if (idx >= 0) {
          const copy = [...prev]
          copy[idx] = saved
          return copy
        }
        return [...prev, saved]
      })
      addToast(editingId ? 'Ingredient updated' : 'Ingredient saved', 'success')
      cancelForm()
    } catch {
      addToast('Failed to save ingredient', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    setDeletingId(id)
    try {
      const ok = await deleteIngredient(departmentId, id)
      if (ok) {
        setIngredients(prev => prev.filter(i => i.id !== id))
        addToast('Ingredient deleted', 'success')
      } else {
        addToast('Failed to delete ingredient', 'error')
      }
    } catch {
      addToast('Failed to delete ingredient', 'error')
    } finally {
      setDeletingId(null)
      setConfirmDeleteId(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search ingredients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> Add Ingredient
        </button>
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              {editingId ? 'Edit Ingredient' : 'New Ingredient'}
            </h3>
            <button onClick={cancelForm} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => updateForm('name', e.target.value)}
                placeholder="e.g. Cake Flour"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Purchase Unit */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Purchase Unit</label>
              <input
                type="text"
                value={form.purchaseUnit}
                onChange={e => updateForm('purchaseUnit', e.target.value)}
                placeholder="e.g. 25kg bag"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Purchase Qty */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Purchase Qty</label>
              <input
                type="number"
                min="0"
                step="any"
                value={form.purchaseQty}
                onChange={e => updateForm('purchaseQty', e.target.value)}
                placeholder="1"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Purchase Cost */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Purchase Cost (ZAR)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.purchaseCost}
                onChange={e => updateForm('purchaseCost', e.target.value)}
                placeholder="0.00"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Usable Unit */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Usable Unit</label>
              <div className="relative">
                <select
                  value={form.usableUnit}
                  onChange={e => updateForm('usableUnit', e.target.value)}
                  className="w-full appearance-none bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
                >
                  {USABLE_UNITS.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>

            {/* Usable Qty Per Unit */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Usable Qty per Unit *</label>
              <input
                type="number"
                min="0"
                step="any"
                value={form.usableQtyPerUnit}
                onChange={e => updateForm('usableQtyPerUnit', e.target.value)}
                placeholder="e.g. 25000"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Waste % */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Waste %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.wastePercent}
                onChange={e => updateForm('wastePercent', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
              {Number(form.wastePercent) === 0 && (
                <p className="flex items-center gap-1 text-xs text-yellow-500 mt-1">
                  <AlertTriangle size={12} /> Waste is 0% — is this correct?
                </p>
              )}
            </div>

            {/* Supplier */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Supplier</label>
              <input
                type="text"
                value={form.supplier}
                onChange={e => updateForm('supplier', e.target.value)}
                placeholder="Optional"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* Purchase Date */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Purchase Date</label>
              <DateInput value={form.purchaseDate} onChange={v => updateForm('purchaseDate', v)} />
            </div>
          </div>

          {/* Live cost preview */}
          <div className="bg-gray-900 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-gray-400">Cost per usable unit ({form.usableUnit})</span>
            <span className="text-lg font-bold text-orange-400">{fmtR(previewCost)}</span>
          </div>

          {/* Form actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? <Spinner /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelForm}
              className="text-gray-400 hover:text-white text-sm px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Ingredients List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Package size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-500">
            {ingredients.length === 0
              ? 'No ingredients yet. Add your first ingredient above.'
              : 'No ingredients match your search.'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Desktop header */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_120px_120px_100px_100px] gap-2 px-4 py-3 text-xs text-gray-500 uppercase tracking-wide border-b border-gray-700">
            <span>Name</span>
            <span>Purchase Cost</span>
            <span>Cost / Unit</span>
            <span>Waste %</span>
            <span className="text-right">Actions</span>
          </div>

          {filtered.map(ing => (
            <div
              key={ing.id}
              className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_100px_100px] gap-2 px-4 py-3 border-b border-gray-700 hover:bg-gray-700/50 transition-colors items-center"
            >
              <div>
                <p className="text-white font-medium text-sm">{ing.name}</p>
                <p className="text-xs text-gray-500">
                  {ing.purchaseUnit || '—'}{ing.supplier ? ` · ${ing.supplier}` : ''}
                </p>
              </div>
              <div className="text-sm text-gray-300">
                <span className="sm:hidden text-xs text-gray-500 mr-1">Cost:</span>
                {fmtR(ing.purchaseCost)}
              </div>
              <div className="text-sm font-medium text-orange-400">
                <span className="sm:hidden text-xs text-gray-500 mr-1">Per {ing.usableUnit}:</span>
                {fmtR(ing.costPerUsableUnit)}/{ing.usableUnit}
              </div>
              <div className="text-sm text-gray-400">
                <span className="sm:hidden text-xs text-gray-500 mr-1">Waste:</span>
                {Number(ing.wastePercent || 0).toFixed(1)}%
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => openEdit(ing)}
                  className="p-1.5 text-gray-400 hover:text-orange-400 transition-colors"
                  title="Edit"
                >
                  <Edit2 size={15} />
                </button>
                {confirmDeleteId === ing.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(ing.id)}
                      disabled={deletingId === ing.id}
                      className="text-xs text-red-400 hover:text-red-300 font-medium"
                    >
                      {deletingId === ing.id ? '...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-xs text-gray-500 hover:text-gray-300"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(ing.id)}
                    className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 2: RECIPES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function RecipesTab({ departmentId, ingredients, recipes, setRecipes, addToast }) {
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ ...EMPTY_RECIPE })
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [ingSearch, setIngSearch] = useState('')
  const [ingDropdownOpen, setIngDropdownOpen] = useState(false)

  // Calculated totals
  const totals = useMemo(() => {
    return calcRecipeTotals(form.ingredients, form.portions, form.targetMargin)
  }, [form.ingredients, form.portions, form.targetMargin])

  function openNew() {
    setForm({ ...EMPTY_RECIPE, ingredients: [] })
    setEditingId(null)
    setShowForm(true)
  }

  function openEdit(recipe) {
    setForm({
      name: recipe.name || '',
      ingredients: recipe.ingredients || [],
      portions: recipe.portions || 1,
      targetMargin: recipe.targetMargin || 65,
    })
    setEditingId(recipe.id)
    setShowForm(true)
  }

  function cancelForm() {
    setShowForm(false)
    setEditingId(null)
    setForm({ ...EMPTY_RECIPE, ingredients: [] })
  }

  function addIngredientLine(ing) {
    setForm(prev => ({
      ...prev,
      ingredients: [
        ...prev.ingredients,
        {
          ingredientId: ing.id,
          name: ing.name,
          usableUnit: ing.usableUnit,
          costPerUsableUnit: ing.costPerUsableUnit || 0,
          qty: '',
          cost: 0,
        },
      ],
    }))
    setIngSearch('')
    setIngDropdownOpen(false)
  }

  function updateIngLine(idx, qty) {
    setForm(prev => {
      const lines = [...prev.ingredients]
      const line = { ...lines[idx] }
      line.qty = qty
      line.cost = (Number(qty) || 0) * (Number(line.costPerUsableUnit) || 0)
      lines[idx] = line
      return { ...prev, ingredients: lines }
    })
  }

  function removeIngLine(idx) {
    setForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== idx),
    }))
  }

  async function handleSave() {
    if (!form.name.trim()) {
      addToast('Recipe name is required', 'error')
      return
    }
    if (form.ingredients.length === 0) {
      addToast('Add at least one ingredient', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = editingId ? { ...form, id: editingId } : { ...form }
      const saved = await saveRecipe(departmentId, payload)
      setRecipes(prev => {
        const idx = prev.findIndex(r => r.id === saved.id)
        if (idx >= 0) {
          const copy = [...prev]
          copy[idx] = saved
          return copy
        }
        return [...prev, saved]
      })
      addToast(editingId ? 'Recipe updated' : 'Recipe saved', 'success')
      cancelForm()
    } catch {
      addToast('Failed to save recipe', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id) {
    setDeletingId(id)
    try {
      const ok = await deleteRecipe(departmentId, id)
      if (ok) {
        setRecipes(prev => prev.filter(r => r.id !== id))
        addToast('Recipe deleted', 'success')
      } else {
        addToast('Failed to delete recipe', 'error')
      }
    } catch {
      addToast('Failed to delete recipe', 'error')
    } finally {
      setDeletingId(null)
      setConfirmDeleteId(null)
    }
  }

  const filteredIngredients = useMemo(() => {
    if (!ingSearch.trim()) return ingredients
    const q = ingSearch.toLowerCase()
    return ingredients.filter(i => i.name?.toLowerCase().includes(q))
  }, [ingredients, ingSearch])

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Recipes</h2>
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} /> Add Recipe
        </button>
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              {editingId ? 'Edit Recipe' : 'New Recipe'}
            </h3>
            <button onClick={cancelForm} className="text-gray-400 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Recipe name */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Recipe Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Chocolate Cake"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Ingredient lines */}
          <div className="space-y-2">
            <label className="block text-sm text-gray-400">Ingredients</label>

            {form.ingredients.length > 0 && (
              <div className="space-y-2">
                {form.ingredients.map((line, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-gray-900 rounded-lg p-3"
                  >
                    <span className="text-sm text-white font-medium flex-1 min-w-0 truncate">
                      {line.name}
                    </span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={line.qty}
                        onChange={e => updateIngLine(idx, e.target.value)}
                        placeholder="Qty"
                        className="w-24 bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
                      />
                      <span className="text-xs text-gray-500 w-12">{line.usableUnit}</span>
                      <span className="text-sm font-medium text-orange-400 w-20 text-right">
                        {fmtR(line.cost)}
                      </span>
                      <button
                        onClick={() => removeIngLine(idx)}
                        className="p-1 text-gray-500 hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add ingredient search */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={ingSearch}
                    onChange={e => {
                      setIngSearch(e.target.value)
                      setIngDropdownOpen(true)
                    }}
                    onFocus={() => setIngDropdownOpen(true)}
                    placeholder="Search ingredient to add..."
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {ingDropdownOpen && filteredIngredients.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredIngredients.map(ing => (
                    <button
                      key={ing.id}
                      onClick={() => addIngredientLine(ing)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-600 transition-colors flex justify-between"
                    >
                      <span>{ing.name}</span>
                      <span className="text-gray-500 text-xs">
                        {fmtR(ing.costPerUsableUnit)}/{ing.usableUnit}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {ingDropdownOpen && filteredIngredients.length === 0 && ingSearch.trim() && (
                <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-sm text-gray-500 text-center">
                  No ingredients found
                </div>
              )}
            </div>

            {/* Click-away */}
            {ingDropdownOpen && (
              <div className="fixed inset-0 z-0" onClick={() => setIngDropdownOpen(false)} />
            )}
          </div>

          {/* Portions + Margin */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Portions</label>
              <input
                type="number"
                min="1"
                value={form.portions}
                onChange={e => setForm(prev => ({ ...prev, portions: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Target Margin %</label>
              <input
                type="number"
                min="0"
                max="99"
                value={form.targetMargin}
                onChange={e => setForm(prev => ({ ...prev, targetMargin: e.target.value }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Auto-calculated results */}
          <div className="bg-gray-900 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Total Cost</p>
              <p className="text-lg font-bold text-white">{fmtR(totals.totalCost)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Cost / Portion</p>
              <p className="text-lg font-bold text-white">{fmtR(totals.costPerPortion)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Selling Price</p>
              <p className="text-lg font-bold text-orange-400">{fmtR(totals.sellingPrice)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Gross Margin</p>
              <p className="text-lg font-bold text-green-400">{fmtR(totals.grossMargin)}</p>
            </div>
          </div>

          {/* Form actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {saving ? <Spinner /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelForm}
              className="text-gray-400 hover:text-white text-sm px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Recipes List */}
      {recipes.length === 0 ? (
        <div className="text-center py-12">
          <FlaskConical size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-500">No recipes yet. Add your first recipe above.</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="hidden sm:grid sm:grid-cols-[1fr_100px_100px_80px_100px] gap-2 px-4 py-3 text-xs text-gray-500 uppercase tracking-wide border-b border-gray-700">
            <span>Name</span>
            <span>Total Cost</span>
            <span>Per Portion</span>
            <span>Margin</span>
            <span className="text-right">Actions</span>
          </div>

          {recipes.map(recipe => {
            const margin = recipe.sellingPrice > 0
              ? ((recipe.sellingPrice - recipe.costPerPortion) / recipe.sellingPrice * 100)
              : 0
            return (
              <div
                key={recipe.id}
                className="grid grid-cols-1 sm:grid-cols-[1fr_100px_100px_80px_100px] gap-2 px-4 py-3 border-b border-gray-700 hover:bg-gray-700/50 transition-colors items-center"
              >
                <div>
                  <p className="text-white font-medium text-sm">{recipe.name}</p>
                  <p className="text-xs text-gray-500">
                    {(recipe.ingredients || []).length} ingredient{(recipe.ingredients || []).length !== 1 ? 's' : ''} · {recipe.portions || 1} portion{(recipe.portions || 1) !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-sm text-gray-300">
                  <span className="sm:hidden text-xs text-gray-500 mr-1">Total:</span>
                  {fmtR(recipe.totalCost)}
                </div>
                <div className="text-sm text-orange-400 font-medium">
                  <span className="sm:hidden text-xs text-gray-500 mr-1">Per portion:</span>
                  {fmtR(recipe.costPerPortion)}
                </div>
                <div className="text-sm text-gray-400">
                  <span className="sm:hidden text-xs text-gray-500 mr-1">Margin:</span>
                  {margin.toFixed(0)}%
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => openEdit(recipe)}
                    className="p-1.5 text-gray-400 hover:text-orange-400 transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={15} />
                  </button>
                  {confirmDeleteId === recipe.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(recipe.id)}
                        disabled={deletingId === recipe.id}
                        className="text-xs text-red-400 hover:text-red-300 font-medium"
                      >
                        {deletingId === recipe.id ? '...' : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(recipe.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 3: BULK SIMULATION
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function BulkSimulationTab() {
  const [form, setForm] = useState({ ...EMPTY_BULK })

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function reset() {
    setForm({ ...EMPTY_BULK })
  }

  const hasInput = form.packSize && form.numPacks && form.totalCost && form.usageDays
  const results = useMemo(() => {
    if (!hasInput) return null
    return calcBulkBreakdown(form.packSize, form.numPacks, form.totalCost, form.usageDays)
  }, [form.packSize, form.numPacks, form.totalCost, form.usageDays, hasInput])

  const savingsPercent = useMemo(() => {
    if (!results || !form.singleUnitCost || Number(form.singleUnitCost) <= 0) return null
    const single = Number(form.singleUnitCost)
    const bulk = results.costPerUnit
    if (single === 0) return null
    return ((single - bulk) / single * 100)
  }, [results, form.singleUnitCost])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Bulk Simulation</h2>
          <p className="text-sm text-gray-500">Calculator only — not saved to database</p>
        </div>
        <button
          onClick={reset}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 transition-colors"
        >
          <X size={14} /> Reset
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Pack description */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Pack Description</label>
            <input
              type="text"
              value={form.packDescription}
              onChange={e => update('packDescription', e.target.value)}
              placeholder="e.g. Sunflower oil 5L"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Pack size + unit */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Pack Size</label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="any"
                value={form.packSize}
                onChange={e => update('packSize', e.target.value)}
                placeholder="5"
                className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              />
              <div className="relative">
                <select
                  value={form.packUnit}
                  onChange={e => update('packUnit', e.target.value)}
                  className="appearance-none bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm pr-7 focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
                >
                  {USABLE_UNITS.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Number of packs */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Number of Packs</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.numPacks}
              onChange={e => update('numPacks', e.target.value)}
              placeholder="4"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Total cost */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Total Cost Paid (ZAR)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.totalCost}
              onChange={e => update('totalCost', e.target.value)}
              placeholder="0.00"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Usage period */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Expected Usage Period (days)</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.usageDays}
              onChange={e => update('usageDays', e.target.value)}
              placeholder="30"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Single unit cost for comparison */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Single Unit Cost (optional, ZAR)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.singleUnitCost}
              onChange={e => update('singleUnitCost', e.target.value)}
              placeholder="For savings comparison"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">
            {form.packDescription || 'Bulk'} — Breakdown
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total Units</p>
              <p className="text-xl font-bold text-white">
                {results.totalUnits} <span className="text-sm text-gray-500">{form.packUnit}</span>
              </p>
            </div>
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xs text-gray-500">Cost per Unit</p>
              <p className="text-xl font-bold text-orange-400">
                {fmtR(results.costPerUnit)}<span className="text-sm text-gray-500">/{form.packUnit}</span>
              </p>
            </div>
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xs text-gray-500">Daily Cost</p>
              <p className="text-xl font-bold text-white">{fmtR(results.dailyCost)}</p>
            </div>
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xs text-gray-500">Daily Units Used</p>
              <p className="text-xl font-bold text-white">
                {results.dailyUnits} <span className="text-sm text-gray-500">{form.packUnit}/day</span>
              </p>
            </div>
          </div>

          {savingsPercent !== null && (
            <div className={`mt-4 rounded-lg p-3 ${savingsPercent > 0 ? 'bg-green-900/30 border border-green-800' : 'bg-red-900/30 border border-red-800'}`}>
              <p className="text-sm">
                {savingsPercent > 0 ? (
                  <span className="text-green-400 font-medium">
                    Buying bulk saves {savingsPercent.toFixed(1)}% compared to single unit cost ({fmtR(form.singleUnitCost)}/{form.packUnit})
                  </span>
                ) : (
                  <span className="text-red-400 font-medium">
                    Bulk is {Math.abs(savingsPercent).toFixed(1)}% more expensive than single unit cost ({fmtR(form.singleUnitCost)}/{form.packUnit})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {!results && (
        <div className="text-center py-12">
          <Calculator size={40} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-500">Fill in pack size, number of packs, total cost, and usage period to see the breakdown.</p>
        </div>
      )}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TAB 4: SUMMARY DASHBOARD
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function SummaryTab({ ingredients, recipes }) {
  // Stats
  const totalIngredients = ingredients.length
  const totalIngredientSpend = ingredients.reduce((s, i) => s + (Number(i.purchaseCost) || 0), 0)
  const totalRecipes = recipes.length

  const avgMargin = useMemo(() => {
    if (recipes.length === 0) return 0
    const margins = recipes.map(r => {
      if (!r.sellingPrice || r.sellingPrice <= 0) return 0
      return ((r.sellingPrice - r.costPerPortion) / r.sellingPrice) * 100
    })
    return margins.reduce((s, m) => s + m, 0) / margins.length
  }, [recipes])

  // Top 5 most expensive ingredients by cost per usable unit
  const top5Expensive = useMemo(() => {
    return [...ingredients]
      .sort((a, b) => (Number(b.costPerUsableUnit) || 0) - (Number(a.costPerUsableUnit) || 0))
      .slice(0, 5)
  }, [ingredients])

  // Recipes ranked by margin
  const recipesByMargin = useMemo(() => {
    return [...recipes]
      .map(r => {
        const margin = r.sellingPrice > 0
          ? ((r.sellingPrice - r.costPerPortion) / r.sellingPrice) * 100
          : 0
        return { ...r, marginPct: margin }
      })
      .sort((a, b) => b.marginPct - a.marginPct)
  }, [recipes])

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Summary Dashboard</h2>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <p className="text-xs text-gray-500 mb-1">Ingredients Tracked</p>
          <p className="text-2xl font-bold text-white">{totalIngredients}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <p className="text-xs text-gray-500 mb-1">Total Ingredient Spend</p>
          <p className="text-2xl font-bold text-orange-400">{fmtR(totalIngredientSpend)}</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <p className="text-xs text-gray-500 mb-1">Average Recipe Margin</p>
          <p className="text-2xl font-bold text-green-400">{avgMargin.toFixed(1)}%</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <p className="text-xs text-gray-500 mb-1">Total Recipes</p>
          <p className="text-2xl font-bold text-white">{totalRecipes}</p>
        </div>
      </div>

      {/* Top 5 expensive ingredients */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">Top 5 Most Expensive Ingredients (by cost per usable unit)</h3>
        </div>
        {top5Expensive.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">No ingredients yet</div>
        ) : (
          top5Expensive.map((ing, idx) => (
            <div
              key={ing.id}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-700 hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-600 w-5">{idx + 1}</span>
                <div>
                  <p className="text-sm text-white font-medium">{ing.name}</p>
                  <p className="text-xs text-gray-500">{ing.purchaseUnit || '—'}</p>
                </div>
              </div>
              <span className="text-sm font-bold text-orange-400">
                {fmtR(ing.costPerUsableUnit)}/{ing.usableUnit}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Recipes ranked by margin */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">Recipes Ranked by Margin</h3>
        </div>
        {recipesByMargin.length === 0 ? (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">No recipes yet</div>
        ) : (
          recipesByMargin.map(recipe => (
            <div
              key={recipe.id}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-700 hover:bg-gray-700/50 transition-colors"
            >
              <div>
                <p className="text-sm text-white font-medium">{recipe.name}</p>
                <p className="text-xs text-gray-500">
                  Cost: {fmtR(recipe.costPerPortion)} · Sell: {fmtR(recipe.sellingPrice)}
                </p>
              </div>
              <span className={`text-sm font-bold ${recipe.marginPct >= 60 ? 'text-green-400' : recipe.marginPct >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                {recipe.marginPct.toFixed(1)}%
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
